import React, { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useCaptureEvent, useDebounce, useLocalStorage } from '@/hooks'
import { checkForScrollBar } from '@/utils'

import { DropdownMenuItem } from '../dropdown-menu'
import { CHECKBOX_COLUMN_WIDTH } from './table.constants'
import * as S from './table.styled'
import { getColumnId, TableProps } from './table.types'
import { getColumnWidth, measureHeaderMinWidth } from './table.utils'
import { TableBody } from './table-body'
import { TableBulkMenu } from './table-bulk-menu'
import { TableCollapsableRow } from './table-collapsable-row'
import { TableHead } from './table-head'
import { TableRow } from './table-row'

// TODO: Refactor table and split internal components into separate directories - it's too complex to maintain in one file - especially the styles
// TODO: Make text in table automatic - font, weight, color should be set automatically to avoid layout changes between pages
export const Table = <T,>({
  id,
  className,
  columns,
  data,
  emptyText,
  emptyDescription,
  error,
  getRowKey,
  loading,
  enablePinning = true,
  mode = 'regular',
  sortBy,
  sortOrder,
  onSort,
  bulkActions,
  rowActions,
  onClearFilters,
  renderCollapsableRow,
  selectAllMatching,
}: TableProps<T>) => {
  const { captureEvent } = useCaptureEvent()

  // Note: v2 - storage now keys on column id (see TableColumn.id / getColumnId),
  // not the (possibly translated) title. Bumping the version discards any
  // stale v1 entry instead of leaving it stuck matching nothing.
  const [pinnedColumnId, setPinnedColumnId] = useLocalStorage<string | null>(
    `table-pinned-${id}`,
    null,
    'v2',
  )
  const [bulkSelectedRowKeys, setBulkSelectedRowKeys] = useState<(string | number)[]>([])
  const [expandedRowKey, setExpandedRowKey] = useState<string | number | null>(null)
  const [hasVerticalScroll, setHasVerticalScroll] = useState(false)
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false)
  // Note: px, keyed by column id - see measureHeaderMinWidth / getColumnWidth.
  const [headerMinWidths, setHeaderMinWidths] = useState<Record<string, number>>({})
  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const hasRestoredScrollRef = useRef(false)
  const { debounce } = useDebounce({ delay: 200, unmountCleanup: false })

  // Note: Cross-page "select all matching the current filter" state - see selectAllMatching.
  const [isSelectAllMatchingActive, setIsSelectAllMatchingActive] = useState(false)
  const [resolvedAllItems, setResolvedAllItems] = useState<T[] | null>(null)
  const [isResolvingAll, setIsResolvingAll] = useState(false)
  const resolveAllAbortRef = useRef<AbortController | null>(null)
  const prevSelectAllMatchingTotalRef = useRef(selectAllMatching?.total)

  const isBulkMode = mode === 'bulk'
  const fillHeight = Boolean(loading || !!error || data.length === 0)

  // Note: Disable pinning when prop disables the feature. In bulk mode, pinning still works -
  // the checkbox column becomes sticky alongside the pinned column, see CheckboxHeaderCell /
  // CheckboxBodyCell.
  const effectivePinnedColumnId = !enablePinning ? null : pinnedColumnId

  // Note: Reorder columns to put the pinned column first
  const orderedColumns = useMemo(() => {
    if (!effectivePinnedColumnId) return columns

    const pinnedColumn = columns.find((col) => getColumnId(col) === effectivePinnedColumnId)
    if (!pinnedColumn) return columns

    const otherColumns = columns.filter((col) => getColumnId(col) !== effectivePinnedColumnId)
    return [pinnedColumn, ...otherColumns]
  }, [columns, effectivePinnedColumnId])

  // Note: one width per column, shared by its <col>, header cell and (pinned) body cells.
  const columnWidths = useMemo(
    () =>
      Object.fromEntries(
        orderedColumns.map((column) => {
          const columnId = getColumnId(column)
          return [columnId, getColumnWidth(column, enablePinning, headerMinWidths[columnId])]
        }),
      ),
    [orderedColumns, enablePinning, headerMinWidths],
  )

  // Note: Selection state calculations
  const allRowKeys = useMemo(() => data.map((item) => getRowKey(item)), [data, getRowKey])
  const isAllSelected = isSelectAllMatchingActive
    ? bulkSelectedRowKeys.length === (selectAllMatching?.total ?? 0)
    : allRowKeys.length > 0 && bulkSelectedRowKeys.length === allRowKeys.length
  const isIndeterminate = isSelectAllMatchingActive
    ? bulkSelectedRowKeys.length > 0 && bulkSelectedRowKeys.length < (selectAllMatching?.total ?? 0)
    : bulkSelectedRowKeys.length > 0 && bulkSelectedRowKeys.length < allRowKeys.length

  const handleClearSelection = () => {
    setIsSelectAllMatchingActive(false)
    setResolvedAllItems(null)
    setBulkSelectedRowKeys([])
  }

  const handleSelectAll = async (checked: boolean) => {
    if (!checked) {
      handleClearSelection()
      return
    }

    if (!selectAllMatching) {
      setBulkSelectedRowKeys(allRowKeys)
      return
    }

    setIsSelectAllMatchingActive(true)
    setIsResolvingAll(true)
    resolveAllAbortRef.current?.abort()
    const controller = new AbortController()
    resolveAllAbortRef.current = controller

    try {
      const items = await selectAllMatching.resolveAll(controller.signal)
      if (controller.signal.aborted) return
      setResolvedAllItems(items)
      setBulkSelectedRowKeys(items.map(getRowKey))
    } catch {
      if (controller.signal.aborted) return
      // Note: resolution failed - fall back to unchecked rather than leaving a half-broken
      // "selected" state with no resolved items.
      setIsSelectAllMatchingActive(false)
    } finally {
      if (!controller.signal.aborted) setIsResolvingAll(false)
    }
  }

  const handleSelectRow = (rowKey: string | number, checked: boolean) => {
    if (checked) {
      setBulkSelectedRowKeys((keys) => [...keys, rowKey])
      if (isSelectAllMatchingActive) {
        // Note: re-checking a row after it was unchecked while select-all-matching is active.
        // The item is guaranteed to still be in `data` (current page) since that's the only
        // place a row checkbox can be clicked from.
        const item = data.find((d) => getRowKey(d) === rowKey)
        if (item) setResolvedAllItems((items) => (items ? [...items, item] : items))
      }
    } else {
      setBulkSelectedRowKeys((keys) => keys.filter((key) => key !== rowKey))
      if (isSelectAllMatchingActive) {
        setResolvedAllItems((items) => items?.filter((d) => getRowKey(d) !== rowKey) ?? items)
      }
    }
  }

  // Note: Get selected items for bulk actions
  const selectedItems = useMemo(() => {
    if (isSelectAllMatchingActive && resolvedAllItems) {
      return resolvedAllItems
    }
    return data.filter((item) => bulkSelectedRowKeys.includes(getRowKey(item)))
  }, [data, getRowKey, bulkSelectedRowKeys, isSelectAllMatchingActive, resolvedAllItems])

  // Note: Check if row actions should be shown (not in bulk mode with selections)
  const hasRowActions = !!rowActions && rowActions.length > 0
  const primaryRowAction = rowActions?.find((action) => action.primary)
  const nonPrimaryRowActions = rowActions?.filter((action) => !action.primary) ?? []
  const showRowActions = hasRowActions && nonPrimaryRowActions.length > 0

  const collapsableRows = useMemo(
    () =>
      renderCollapsableRow
        ? data.map((item, index) => renderCollapsableRow(item, index) ?? null)
        : data.map(() => null),
    [data, renderCollapsableRow],
  )

  const getRowActionMenuItems = (item: T): DropdownMenuItem[] => {
    if (!rowActions) return []

    return rowActions.map((action) => ({
      disabled: action.disabled?.(item),
      hidden: action.hidden?.(item),
      icon: action.icon,
      label: action.label,
      loading: action.loading?.(item),
      onClick: () => action.onClick(item),
      variant: action.variant,
    }))
  }

  const columnsCount = orderedColumns.length + (isBulkMode ? 1 : 0) + (showRowActions ? 1 : 0)

  const handlePinColumn = (columnId: string | null) => {
    setPinnedColumnId(columnId)
    captureEvent('user_action', {
      name: 'table_pin_column',
      screen: id ?? '',
      value: columnId,
    })
  }

  const handleTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop
    debounce(() => {
      try {
        localStorage.setItem(`table-scroll-${id}`, String(scrollTop))
      } catch {
        // Note: localStorage may be unavailable (private browsing, quota, disabled) -
        // scroll persistence is a nice-to-have, fail silently.
      }
    })
  }

  useLayoutEffect(() => {
    const tableWrapperElement = tableWrapperRef.current
    const tableElement = tableRef.current
    if (!tableWrapperElement || !tableElement) return

    const checkScrollable = () => {
      setHasVerticalScroll(checkForScrollBar(tableWrapperElement, 'vertical'))
      setHasHorizontalScroll(checkForScrollBar(tableWrapperElement, 'horizontal'))
    }

    checkScrollable()

    const observer = new ResizeObserver(checkScrollable)
    // NOTE:
    // - Check for `tableWrapperElement` if the table wrapper is resized
    // - Check for `tableElement` if the table's content is changed, e.g., a new item added (or removed) and now can be scrolled (not)
    observer.observe(tableWrapperElement)
    observer.observe(tableElement)
    return () => observer.disconnect()
  }, [])

  // Note: measures how wide each header needs to be for its title and actions. Re-measured when
  // the columns change (titles, sorting, pinning), when the header resizes (e.g. crossing the
  // mobile breakpoint changes the th padding) and once web fonts finish loading - the result
  // doesn't depend on the column widths themselves, so updating them can't loop.
  useLayoutEffect(() => {
    const tableHead = tableRef.current?.tHead
    if (!tableHead) return
    let isActive = true

    const measure = () => {
      if (!isActive) return
      const next: Record<string, number> = {}
      tableHead.querySelectorAll<HTMLElement>('th[data-column-id]').forEach((headerCell) => {
        next[headerCell.dataset.columnId!] = measureHeaderMinWidth(headerCell)
      })
      setHeaderMinWidths((prev) => {
        const keys = Object.keys(next)
        const isSame =
          keys.length === Object.keys(prev).length && keys.every((key) => prev[key] === next[key])
        return isSame ? prev : next
      })
    }

    measure()
    document.fonts?.ready.then(measure)
    const observer = new ResizeObserver(measure)
    observer.observe(tableHead)
    return () => {
      isActive = false
      observer.disconnect()
    }
  }, [orderedColumns, enablePinning])

  // Note: Cleanup the selected rows when the data changes.
  // It's possible that when filters got changed, the selected rows are not valid anymore.
  // Or when an action is performed, the selected rows are not valid anymore.
  // Skipped while select-all-matching is active - that selection is page-independent by design
  // (see the effect below), so a page nav alone (new `data`, same filters) must not clear it.
  useEffect(() => {
    if (isSelectAllMatchingActive) return
    setBulkSelectedRowKeys([])
  }, [data])

  // Note: Restores this table's last scrollTop once real rows are in the DOM - mirrors
  // TableBody's own render gate (!loading && !error && data.length > 0). `loading` (isFetching)
  // is briefly true even on a warm react-query cache (e.g. right after navigating Back), when
  // only the loading spinner is rendered - matching the gate avoids silently burning the
  // one-shot restore against an empty wrapper. useLayoutEffect (not useEffect) so the jump
  // happens before paint - no flash of a scrolled-to-top table before it snaps to the restored
  // offset.
  useLayoutEffect(() => {
    if (hasRestoredScrollRef.current || loading || error || !data.length) return
    const tableWrapperElement = tableWrapperRef.current
    if (!tableWrapperElement) return

    hasRestoredScrollRef.current = true
    try {
      const saved = localStorage.getItem(`table-scroll-${id}`)
      const scrollTop = saved ? Number(saved) : NaN
      if (Number.isFinite(scrollTop)) tableWrapperElement.scrollTop = scrollTop
    } catch {
      // Note: see handleTableScroll - localStorage may be unavailable, fail silently.
    }
  }, [data, loading, error, id])

  // Note: select-all-matching's own selection is scoped to the current filter, not to any one
  // page, so it can't key off `data` (changes on every page nav too). `total` is a
  // server-computed count already scoped by every active filter/search/status - a page nav
  // alone never changes it, but a filter/search/status change does - so it's used here as the
  // "did the filter change" signal instead. This is an indirect proxy, not a true filter-identity
  // comparison; fine for every filter this table currently supports, but a future consumer whose
  // filter change could change WHICH rows match without changing the total COUNT would need a
  // different signal.
  useEffect(() => {
    if (selectAllMatching && prevSelectAllMatchingTotalRef.current !== selectAllMatching.total) {
      handleClearSelection()
    }
    prevSelectAllMatchingTotalRef.current = selectAllMatching?.total
  }, [selectAllMatching?.total])

  return (
    <S.Container className={className} $fillHeight={fillHeight}>
      <S.Wrapper
        ref={tableWrapperRef}
        $hasVerticalScroll={hasVerticalScroll}
        onScroll={handleTableScroll}
      >
        <S.Table
          ref={tableRef}
          $hasVerticalScroll={hasVerticalScroll}
          $hasHorizontalScroll={hasHorizontalScroll}
          $fillHeight={fillHeight}
        >
          <colgroup>
            {isBulkMode && (
              <col
                style={{
                  maxWidth: CHECKBOX_COLUMN_WIDTH,
                  minWidth: CHECKBOX_COLUMN_WIDTH,
                  width: CHECKBOX_COLUMN_WIDTH,
                }}
              />
            )}
            {orderedColumns.map((column) => (
              <col key={getColumnId(column)} style={{ width: columnWidths[getColumnId(column)] }} />
            ))}
            {/* Note: Actions column has zero width - button is absolutely positioned */}
            {showRowActions && <col style={{ width: 0 }} />}
          </colgroup>
          <TableHead
            columnWidths={columnWidths}
            columns={orderedColumns}
            data={data}
            enablePinning={enablePinning}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            isSelectAllLoading={isResolvingAll}
            mode={mode}
            pinnedColumnId={effectivePinnedColumnId}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onPinColumn={handlePinColumn}
            onSelectAll={handleSelectAll}
            onSort={onSort}
          />
          <TableBody
            columnsAmount={columnsCount}
            emptyText={emptyText}
            emptyDescription={emptyDescription}
            error={error}
            loading={loading}
            onClearFilters={onClearFilters}
          >
            {data.map((item, index) => {
              const rowKey = getRowKey(item)
              const isSelected = bulkSelectedRowKeys.includes(rowKey)
              const menuItems = showRowActions ? getRowActionMenuItems(item) : []
              const collapsableRow = collapsableRows[index]
              const hasCollapsableRow = !!collapsableRow
              const isExpanded = hasCollapsableRow && expandedRowKey === rowKey

              const handleToggleExpand = () => {
                setExpandedRowKey((current) => (current === rowKey ? null : rowKey))
              }

              return (
                <Fragment key={rowKey}>
                  <TableRow
                    columnWidths={columnWidths}
                    columns={orderedColumns}
                    hasCollapsableRow={hasCollapsableRow}
                    isBulkMode={isBulkMode}
                    isExpanded={isExpanded}
                    isSelected={isSelected}
                    item={item}
                    menuItems={menuItems}
                    pinnedColumnId={effectivePinnedColumnId}
                    rowAction={primaryRowAction}
                    rowKey={rowKey}
                    showRowActions={showRowActions}
                    onSelectRow={handleSelectRow}
                    onToggleExpand={handleToggleExpand}
                  />
                  {hasCollapsableRow && (
                    <TableCollapsableRow isOpen={isExpanded} colSpan={columnsCount}>
                      {collapsableRow}
                    </TableCollapsableRow>
                  )}
                </Fragment>
              )
            })}
          </TableBody>
        </S.Table>
      </S.Wrapper>
      {isBulkMode && bulkSelectedRowKeys.length > 0 && (
        <TableBulkMenu
          bulkActions={bulkActions}
          selectedCount={bulkSelectedRowKeys.length}
          selectedItems={selectedItems}
          onClearSelection={handleClearSelection}
        />
      )}
    </S.Container>
  )
}
